import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Star, Clock, Users, Award, Target, Code, Smartphone } from 'lucide-react';

const Offers: React.FC = () => {
  const packages = [
    {
      name: 'Starter',
      description: 'Perfect for small luxury brands looking to establish their digital presence',
      price: '$5,000',
      period: 'month',
      icon: Target,
      color: 'from-primary-500 to-primary-600',
      features: [
        'Digital Marketing Strategy',
        'Social Media Management',
        'Content Creation (4 posts/week)',
        'Basic SEO Optimization',
        'Monthly Performance Reports',
        'Email Support',
        '1 Strategy Call/Month'
      ],
      popular: false,
      cta: 'Get Started'
    },
    {
      name: 'Professional',
      description: 'Ideal for established luxury brands ready to scale their digital efforts',
      price: '$15,000',
      period: 'month',
      icon: Code,
      color: 'from-accent-500 to-accent-600',
      features: [
        'Everything in Starter',
        'Advanced SEO & SEM',
        'Custom Website Development',
        'E-commerce Integration',
        'Advanced Analytics',
        'Priority Support',
        '2 Strategy Calls/Month',
        'Dedicated Account Manager',
        'A/B Testing & Optimization'
      ],
      popular: true,
      cta: 'Most Popular'
    },
    {
      name: 'Enterprise',
      description: 'Comprehensive solution for large luxury brands with complex needs',
      price: 'Custom',
      period: 'pricing',
      icon: Smartphone,
      color: 'from-primary-600 to-accent-500',
      features: [
        'Everything in Professional',
        'Custom Mobile App Development',
        'Multi-platform Integration',
        'Advanced Marketing Automation',
        'White-label Solutions',
        '24/7 Priority Support',
        'Weekly Strategy Calls',
        'Dedicated Team',
        'Custom Reporting Dashboard',
        'API Development & Integration'
      ],
      popular: false,
      cta: 'Contact Us'
    }
  ];

  const addOns = [
    {
      name: 'Brand Identity Package',
      description: 'Complete brand identity design including logo, guidelines, and visual assets',
      price: '$8,500',
      icon: Star,
      features: ['Logo Design', 'Brand Guidelines', 'Visual Identity Kit', 'Business Card Design']
    },
    {
      name: 'Photography & Videography',
      description: 'Professional photography and videography for your luxury brand',
      price: '$12,000',
      icon: Award,
      features: ['Product Photography', 'Brand Video', 'Social Media Content', 'Lifestyle Shoots']
    },
    {
      name: 'Advanced Analytics',
      description: 'Comprehensive analytics setup and custom reporting dashboard',
      price: '$3,500',
      icon: Target,
      features: ['Custom Dashboard', 'Advanced Tracking', 'Monthly Insights', 'ROI Analysis']
    },
    {
      name: 'Content Marketing',
      description: 'Premium content creation and marketing strategy',
      price: '$6,000',
      icon: Users,
      features: ['Blog Writing', 'Video Content', 'Social Media Content', 'Email Campaigns']
    }
  ];

  const guarantees = [
    {
      icon: Clock,
      title: '24-Hour Response',
      description: 'We respond to all inquiries within 24 hours, guaranteed.'
    },
    {
      icon: Award,
      title: 'Quality Guarantee',
      description: '100% satisfaction guarantee on all our services.'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Personal account manager for all Professional and Enterprise clients.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold mb-6">
              Our <span className="text-gradient">Premium Offers</span>
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Choose the perfect package for your brand. All our offers include our premium service guarantee and are designed to deliver exceptional results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Choose Your Package</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Flexible packages designed to meet the unique needs of luxury brands at every stage of their digital journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className={`relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  pkg.popular ? 'ring-2 ring-primary-500 scale-105' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-primary-500 to-accent-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  </div>
                )}

                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-r ${pkg.color} rounded-xl flex items-center justify-center mb-6`}>
                  <pkg.icon className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-serif font-semibold mb-4">{pkg.name}</h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">{pkg.description}</p>

                {/* Price */}
                <div className="mb-8">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {pkg.price}
                    <span className="text-lg text-neutral-500">/{pkg.period}</span>
                  </div>
                  {pkg.price === 'Custom' && (
                    <div className="text-sm text-neutral-500">Contact us for pricing</div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                      <span className="text-neutral-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to="/contact"
                  className={`w-full py-3 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center ${
                    pkg.popular
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {pkg.cta}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Premium Add-ons</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Enhance your package with these premium services designed specifically for luxury brands.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {addOns.map((addon, index) => (
              <motion.div
                key={addon.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <addon.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{addon.name}</h3>
                    <div className="text-2xl font-bold text-primary-600 mb-2">{addon.price}</div>
                  </div>
                </div>

                <p className="text-neutral-600 mb-6 leading-relaxed">{addon.description}</p>

                <ul className="space-y-2">
                  {addon.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-primary-500 flex-shrink-0" />
                      <span className="text-sm text-neutral-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Guarantees</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We stand behind our work with these guarantees to ensure your complete satisfaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {guarantees.map((guarantee, index) => (
              <motion.div
                key={guarantee.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <guarantee.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{guarantee.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{guarantee.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary-500 to-accent-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
          >
            <h2 className="text-4xl font-serif font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Choose your package or contact us for a custom solution tailored to your luxury brand's needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-accent-50 transition-colors duration-300 inline-flex items-center justify-center">
                Get Free Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/services" className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition-colors duration-300 inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Offers;
